import React, { FC, memo, useEffect, useId, useState } from 'react'

import { useForm } from 'react-hook-form'

import * as yup from 'yup'

import { yupResolver } from '@hookform/resolvers/yup'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'

import { copyToClipboardOnAndroid } from '../../../utils'
import { useBodyDisableScroll } from '../../../utils/hooks'

import { WidgetButton } from '../components/WidgetButton'
import WidgetDemo from '../components/WidgetDemo'
import WidgetThemeColors from '../components/WidgetThemeColors'
import { toast } from 'App'
import { AnimateHeight } from 'components/AnimateHeight/AnimateHeight'
import ButtonIcon from 'components/ButtonIcon'
import InnerLoading from 'components/InnerLoading'
import Modal from 'components/Modal'
import UserInfoInput from 'components/UserInfoInput'

import { useStore } from '../../../store'

import styles from './styles.module.scss'

const WidgetSchema = yup.object().shape({
  name: yup
    .string()
    .required('This field is required')
    .min(2, 'Name should be longer than 2 letters')
    .test(
      'symbol@',
      'You are not allowed to use symbol "@"',
      (value, context) => {
        const str = value as string
        return !str.match(/@/);
      }
    ),
  website: yup
    .string()
    .required('This field is required')
    .min(2, 'Website should be longer than 2 letters')
    .matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter correct url'
    )
    .test(
      'whitespace',
      'You are not allowed to use whitespace',
      (value, context) => {
        const str = value as string
        return !str.match(/^$|\s+/);
        // return !str.trim().match(/^$|\s+/)
      }
    )
    .test(
      'symbol@',
      'You are not allowed to use symbol "@"',
      (value, context) => {
        const str = value as string
        return !str.match(/@/);
      }
    ),
  width: yup
    .number()
    .min(300, 'The width should be greater than 300')
    .required('This field is required'),
  height: yup
    .number()
    .min(350, 'The height should be greater than 350')
    .required('This field is required'),
  postCount: yup
    .number()
    .min(3, 'The publication count should be greater than 3')
    .required('This field is required'),
  themeBG: yup
    .string()
    .matches(
      /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/,
      'Enter correct color hex code'
    ),
  themeColor: yup
    .string()
    .matches(
      /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/,
      'Enter correct color hex code'
    ),
  themeButtonBG: yup
    .string()
    .test(
      'is-hex-or-empty',
      'Enter correct color hex code or leave it empty',
      (value: any) => {
        return value === '' || /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/.test(value);
      }
    ),
  themeButtonColor: yup
    .string()
    .test(
      'is-hex-or-empty',
      'Enter correct color hex code or leave it empty',
      (value: any) => {
        return value === '' || /^#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})$/.test(value);
      }
    ),
})

interface IProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const WidgetSettings: FC<IProps> = ({ isOpen, setIsOpen }) => {
  const { widget, theme } = useStore()

  useBodyDisableScroll(true)

  const [showCode, setShowCode] = useState(false)

  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [width, setWidth] = useState('800')
  const [height, setHeight] = useState('500')
  const [postCount, setPostCount] = useState('5')
  const [themeType, setThemeType] = useState('light')
  const [themeBG, setThemeBG] = useState('#fbfbfb')
  const [themeColor, setThemeColor] = useState('#2e2b63')
  const [themeButtonBG, setThemeButtonBG] = useState('#1f45c3')
  const [themeButtonColor, setThemeButtonColor] = useState('#ffffff')
  const [themeBorder, setThemeBorder] = useState(true)

  const [apiKey, setApiKey] = useState('')

  const [codeType, setCodeType] = useState('html')

  const {
    register,
    handleSubmit,
    formState: { errors, isLoading, isSubmitting },
    getValues,
    reset,
    resetField,
  } = useForm({
    resolver: yupResolver(WidgetSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: '',
      website: '',
      width: 800,
      height: 500,
      postCount: 5,
      themeBG: '#fbfbfb',
      themeColor: '#2e2b63',
      themeButtonBG: '#1f45c3',
      themeButtonColor: '#ffffff',
    },
    resetOptions: {
      keepValues: true,
    },
  })

  useEffect(() => {
    window.addEventListener('beforeunload', widget.reset)

    return () => {
      reset()
      widget.reset()
      window.removeEventListener('beforeunload', widget.reset)
    }
  }, [])

  useEffect(() => {
    if (['light', 'dark'].includes(themeType!)) {
      resetField('themeBG')
      resetField('themeColor')
      resetField('themeButtonBG')
      resetField('themeButtonColor')
      setThemeBorder(true)
    }
  }, [themeType])

  const themeTypeRadioID = useId()
  const codeTypeRadioID = useId()

  const [settingsType, setSettingsType] = useState(0)

  const handleNext = () => {
    setSettingsType(st => st + 1)
  }

  const handleBack = () => {
    setSettingsType(st => Math.max(st - 1, 0))
    widget.reset()
  }

  const toggleShowCode = () => {
    setShowCode(sc => !sc)
  }

  const closeSettingsModal = () => {
    setIsOpen(false)
  }

  const isCustomThemeType = ['customLight', 'customDark'].includes(themeType!)

  const params = new URLSearchParams()
  params.set('count', String(getValues('postCount')))
  params.set('theme', String(themeType))
  if (isCustomThemeType) {
    params.set('themeBG', String(getValues('themeBG')))
    params.set('themeColor', String(getValues('themeColor')))
    params.set('themeButtonBG', String(getValues('themeButtonBG')))
    params.set('themeButtonColor', String(getValues('themeButtonColor')))
    params.set('themeBorder', String(themeBorder))
  }

  const iframeCode = `<iframe
      id="authencityWidget_${apiKey}"
      width="${getValues('width')}"
      height="${getValues('height')}"
      src="${window.location.origin}/widget/${apiKey}?${params}"
      title="Authencity Widget"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
   ></iframe>`

  const htmlCode = `
  <script>
    window.addEventListener('message', (event) => {
      if (
        event &&
        event.data === 'RequestParentURL' &&
        event.source &&
        event.source.postMessage &&
        event.origin
      ) {
        event.source.postMessage(window.location.href, event.origin);
      }
    })
  </script>
  `

  const reactCode = `
  // ⬇︎⬇︎⬇︎ --- insert this code in your React component before "return" --- ⬇︎⬇︎⬇︎
  
  useEffect(() => {
    const sendParentURL = (event) => {
      if (
        event &&
        event.data === 'RequestParentURL' &&
        event.source &&
        event.source.postMessage &&
        event.origin
      ) {
        event.source.postMessage(window.location.href, event.origin);
      }
    };

    window.addEventListener('message', sendParentURL);

    return () => {
      window.removeEventListener('message', sendParentURL);
    };
  }, []);
  
  // ⬆︎⬆︎⬆︎ --- insert this code in your React component before "return" --- ⬆︎⬆︎⬆︎
  `

  const code = `${iframeCode}

   ${codeType === 'react' ? reactCode : htmlCode}
  `

  const copyCode = async () => {
    try {
      await copyToClipboardOnAndroid(code)
      toast({
        type: 'success',
        message: 'The code has been copied',
      })
    } catch (err) {
      toast({
        type: 'error',
        message: `Failed to copy`,
      })
    }
  }

  const handleChangeThemeType = (event: any) => {
    setThemeType(event.target.value)
  }

  const handleChangeCodeType = (event: any) => {
    setCodeType(event.target.value)
  }

  const onSubmit = async () => {
    if (apiKey) {
      widget.reset()
    }

    // preserve values on "Next" and "Back" button
    setName(getValues('name'))
    setWebsite(getValues('website'))
    setWidth(String(getValues('width')))
    setHeight(String(getValues('height')))
    setPostCount(String(getValues('postCount')))
    setThemeBG(String(getValues('themeBG')))
    setThemeColor(String(getValues('themeColor')))
    setThemeButtonBG(String(getValues('themeButtonBG')))
    setThemeButtonColor(String(getValues('themeButtonColor')))

    let url = getValues('website')
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url
    }
    const newURL = new URL(url)
    const newWebsite = newURL.host

    try {
      const key = await widget.getApiKey(getValues('name'), newWebsite)
      if (key?.length > 0) {
        setApiKey(key)

        handleNext()

        toast({
          type: 'success',
          message: 'The API key has been generated successfully',
        })
      } else {
        toast({
          type: 'warning',
          message: 'The API kode is empty',
        })
      }
    } catch (e: any) {
      console.log('get widget api key error', e)

      toast({
        type: 'error',
        message: e?.message || 'Something went wrong',
      })
    }
  }

  const navigateToDocs = () => {
    window.open(
      'https://authencity.io/FAQ?chapterId=964c84e7-e694-4aa7-b50f-88dd69f49591&paragraphId=ef9f7b2b-a5c7-44bc-b4a0-cc122fbc8a16',
      '_blank'
    )
  }

  const widgetSettings = (
    <div className={styles.widgetSettings}>
      {settingsType === 0 && (
        <div className={styles.widgetForm}>
          <div className={styles.modalTitle}>
            <span>Widget settings</span>

            <ButtonIcon
              style={{
                minWidth: '13px',
                width: '13px',
                height: '13px',
                margin: '0 0 1px 3px',
              }}
              styleIcon={{
                width: '7px',
                height: '7px',
              }}
              icon={'info'}
              tooltip={'Click this button to open documentation'}
              tooltipPosition="top"
              tooltipDisablePortal={false}
              bordered
              onClick={navigateToDocs}
            />
          </div>

          <FormControl className={styles.widgetFormControl}>
            <FormLabel id={themeTypeRadioID} className={styles.formLabel}>
              Widget theme:
            </FormLabel>

            <RadioGroup
              row
              aria-labelledby={themeTypeRadioID}
              name="themeType"
              value={themeType}
              onChange={handleChangeThemeType}
              className={styles['widgetFormControl-list']}
            >
              {[
                {
                  key: 'light',
                  value: 'Light',
                },
                {
                  key: 'dark',
                  value: 'Dark',
                },
                {
                  key: 'customLight',
                  value: 'Custom Light',
                },
                {
                  key: 'customDark',
                  value: 'Custom Dark',
                },
              ].map(e => (
                <FormControlLabel
                  key={e.key}
                  value={e.key}
                  control={
                    <Radio
                      sx={{
                        color: theme.theme === 'dark' ? '#ffffff' : '#2E2B63',
                      }}
                    />
                  }
                  label={<span className={styles.formLabel}>{e?.value}</span>}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <form onSubmit={e => e?.preventDefault()}>
            {isCustomThemeType && (
              <WidgetThemeColors
                register={register}
                themeBG={themeBG}
                themeColor={themeColor}
                themeButtonBG={themeButtonBG}
                themeButtonColor={themeButtonColor}
                themeBorder={themeBorder}
                setThemeBorder={setThemeBorder}
              />
            )}

            <UserInfoInput
              placeholder={'e.g. Authencity LTD'}
              title={'Name'}
              edit={true}
              value={name}
              name={'name'}
              register={register}
              error={errors.name?.message}
              styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
            />

            <UserInfoInput
              placeholder={'e.g. authencity.io'}
              title={'Website'}
              edit={true}
              value={website}
              name={'website'}
              register={register}
              error={errors.website?.message}
              styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
            />

            <UserInfoInput
              placeholder={'Enter widget width'}
              title={'Widget width'}
              edit={true}
              value={width}
              name={'width'}
              register={register}
              error={errors.width?.message}
              styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
            />

            <UserInfoInput
              placeholder={'Enter widget height'}
              title={'Widget height'}
              edit={true}
              value={height}
              name={'height'}
              register={register}
              error={errors.height?.message}
              styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
            />

            <UserInfoInput
              placeholder={'Enter publications count'}
              title={'Publications count'}
              edit={true}
              value={postCount}
              name={'postCount'}
              register={register}
              error={errors.postCount?.message}
              styleErrorIcon={{ padding: 0, width: '16px', height: '16px' }}
            />

            <WidgetButton
              onClick={handleSubmit(onSubmit)}
              text={'Next'}
              type={'submit'}
            />
          </form>

          <InnerLoading
            loading={isLoading || isSubmitting}
            style={{
              borderRadius: '16px',
            }}
          />
        </div>
      )}

      {settingsType === 1 && apiKey && (
        <div className={styles['widgetSettings-demo']}>
          <WidgetDemo
            apiKey={apiKey}
            getValues={getValues}
            params={params.toString()}
          />

          <div className={styles.codeWrapper}>
            <div className={styles.codeWrapperButtons}>
              <WidgetButton onClick={handleBack} text={'Back'} />

              <WidgetButton
                onClick={toggleShowCode}
                text={(showCode ? 'Hide' : 'Show') + ' widget code'}
                isDisabled={!apiKey}
              />
            </div>

            <AnimateHeight isOpen={showCode} className={styles.codeModal}>
              <p className={styles.code}>
                <pre>{code}</pre>
              </p>

              <div className={styles.codeButtons}>
                <RadioGroup
                  row
                  aria-labelledby={codeTypeRadioID}
                  name="codeType"
                  value={codeType}
                  onChange={handleChangeCodeType}
                  className={styles['widgetFormControl-list']}
                >
                  {[
                    {
                      key: 'html',
                      value: 'HTML',
                    },
                    {
                      key: 'react',
                      value: 'React / Next.js',
                    },
                  ].map(e => (
                    <FormControlLabel
                      key={e.key}
                      value={e.key}
                      control={
                        <Radio
                          sx={{
                            color:
                              theme.theme === 'dark' ? '#ffffff' : '#2E2B63',
                          }}
                        />
                      }
                      label={
                        <span className={styles.formLabel}>{e?.value}</span>
                      }
                    />
                  ))}
                </RadioGroup>

                <WidgetButton
                  onClick={copyCode}
                  text={'Copy code'}
                  isDisabled={!apiKey || isLoading}
                  w100={false}
                />
              </div>
            </AnimateHeight>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <Modal
      blockOutsideClose
      size={settingsType === 0 ? 'responsiveMedium' : 'responsive'}
      onClose={closeSettingsModal}
      open={isOpen}
      closeButton={true}
      children={widgetSettings}
    />
  )
}

WidgetSettings.displayName = 'WidgetSettings'

export default memo(WidgetSettings)
